import React, { useState, useCallback, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import Preview from './Preview';
import Toolbar from './toolbar';
import FormGenerator from './FormGenerator';
import store from './stores/store';
import Registry from './stores/registry';
import AppLocale from './language-provider';

import FormInfo from '../FormBuilder/FormInfo';
import HelpIcon from '../core/HelpIcon/HelpIcon';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
	FieldLimitAlertContainer,
	FieldLimitErrorAlert,
	DisabledToolboxContainer,
	CursorTooltip
} from './FieldLimitAlert/FieldLimitComponents';

const ReactFormBuilder = ({
	data,
	formId,
	formInfo,
	formTitle,
	readOnly,
	in_use,
	isTemplate,
	isReadyToPublish,
	onLoad,
	onPost,
	renderEditForm,
	setMetaInfoChanged,
	forceRefresh,
	hideToolbox,
	onlyInstructions,
	hasEditPermissions,
	enableSelection,
	selectionOnClick,
	showCorrectColumn,
	toolbarItems,
	customToolbarItems,
	enableAddOnClick,
	show_description,
	refresh,
	locale = 'en',
	files,
	url,
	saveUrl,
	variables,
	saveAlways,
	fieldLimit = null,
	...restProps
}) => {
	const [editMode, setEditMode] = useState(false);
	const [editElement, setEditElement] = useState(null);
	const [fieldLimitInfo, setFieldLimitInfo] = useState({
		current: 0,
		limit: fieldLimit,
		remaining: fieldLimit,
		isLimitReached: false
	});
	const [showLimitError, setShowLimitError] = useState(false);
	const [tooltipOpen, setTooltipOpen] = useState(false);

	useEffect(() => {
		const unsubscribe = store.subscribe(() => {
			const limitInfo = store.getFieldLimitInfo();
			setFieldLimitInfo(limitInfo);

			if (limitInfo.isLimitReached && limitInfo.limit && !showLimitError) {
				setShowLimitError(true);
			}
		});

		return () => {
			if (typeof unsubscribe === 'function') {
				unsubscribe();
			}
		};
	}, [showLimitError]);

	const editModeOn = useCallback(
		(data, e) => {
			e.preventDefault();
			e.stopPropagation();
			if (editMode) {
				setEditMode(!editMode);
				setEditElement(null);
			} else {
				setEditMode(!editMode);
				setEditElement(data);
			}
		},
		[editMode]
	);

	const manualEditModeOff = useCallback(() => {
		if (editMode) {
			setEditMode(false);
			setEditElement(null);
		}
	}, [editMode]);

	const language = locale || 'en';
	const currentAppLocale = AppLocale[language];
	const isToolboxDisabled = !readOnly && fieldLimitInfo.isLimitReached;

	const handleToolboxMouseEnter = useCallback(() => {
		if (isToolboxDisabled) {
			setTooltipOpen(true);
		}
	}, [readOnly, fieldLimitInfo.isLimitReached]);

	const handleToolboxMouseLeave = useCallback(() => {
		setTooltipOpen(false);
	}, []);

	return (
		<IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
			<div style={{ width: '100%' }}>
				{!hideToolbox && hasEditPermissions && fieldLimit && !readOnly && (
					<FieldLimitAlertContainer>
						{fieldLimitInfo.isLimitReached && showLimitError && (
							<FieldLimitErrorAlert
								variant="outlined"
								severity="error"
								action={
									<IconButton
										aria-label="close"
										color="error"
										size="small"
										onClick={() => setShowLimitError(false)}
									>
										<CloseIcon fontSize="inherit" />
									</IconButton>
								}
							>
								{`This form has reached the maximum number of fields supported on a single form (${fieldLimit} fields). Please split your form into multiple forms to add more fields.`}
							</FieldLimitErrorAlert>
						)}
					</FieldLimitAlertContainer>
				)}

				<div className="react-form-builder clearfix">
					<div className="form-builder-outermost-container">
						<div
							className="form-builder-inner-left"
							style={{
								width: !hideToolbox && hasEditPermissions ? '80%' : '100%'
							}}
						>
							<div className="form-builder-title widthMargin">{formTitle || 'Form Layout'}</div>
							<div className="form-builder-inner-container">
								{formInfo && (
									<FormInfo
										data={data}
										url={url}
										saveUrl={saveUrl}
										onLoad={onLoad}
										onPost={onPost}
										formTitle={formTitle}
										locale={locale}
										hideToolbox={hideToolbox}
										readOnly={readOnly}
										hasEditPermissions={hasEditPermissions}
										isTemplate={isTemplate}
										in_use={in_use}
										showCorrectColumn={showCorrectColumn}
										saveAlways={saveAlways}
										enableSelection={enableSelection}
										selectionOnClick={selectionOnClick}
										show_description={show_description}
										toolbarItems={toolbarItems}
										customToolbarItems={customToolbarItems}
										enableAddOnClick={enableAddOnClick}
										files={files}
										variables={variables}
										renderEditForm={renderEditForm}
										formInfo={formInfo}
										formId={formId}
										forceRefresh={forceRefresh}
										isReadyToPublish={isReadyToPublish}
										{...restProps}
									/>
								)}

								<Preview
									files={files}
									manualEditModeOff={manualEditModeOff}
									showCorrectColumn={showCorrectColumn}
									data={data}
									url={url}
									saveUrl={saveUrl}
									onLoad={onLoad}
									onPost={onPost}
									editModeOn={editModeOn}
									editMode={editMode}
									variables={variables}
									registry={Registry}
									editElement={editElement}
									renderEditForm={renderEditForm}
									saveAlways={saveAlways}
									hasBusinessLogic={formInfo?.hasBusinessLogic}
									readOnly={in_use && !isTemplate ? true : readOnly}
									enableSelection={enableSelection}
									selectionOnClick={selectionOnClick}
									hideToolbox={hideToolbox}
									isTemplate={isTemplate}
									isReadyToPublish={isReadyToPublish}
									fieldLimit={fieldLimit}
								/>
							</div>
						</div>

						{!hideToolbox && hasEditPermissions && (
							<CursorTooltip
								title={
									isToolboxDisabled
										? 'Maximum number of fields reached. Remove fields before adding more'
										: ''
								}
								open={tooltipOpen}
								followCursor
								arrow
								placement="top"
							>
								<DisabledToolboxContainer
									disabled={readOnly || fieldLimitInfo.isLimitReached}
									className={`form-builder-inner-right ${
										(readOnly || fieldLimitInfo.isLimitReached) && 'disabled-section'
									}`}
									onMouseEnter={handleToolboxMouseEnter}
									onMouseLeave={handleToolboxMouseLeave}
								>
									<div className="form-builder-title">
										Form Elements Toolbox{' '}
										<HelpIcon
											style={{ right: '20px' }}
											iconStyle={{ fill: 'white', opacity: '1' }}
											hint={'Drag and drop element in the editable area'}
										/>
									</div>

									<Toolbar
										items={toolbarItems}
										showDescription={show_description}
										customItems={customToolbarItems}
										formId={formId}
										forceRefresh={forceRefresh}
										enableAddOnClick={enableAddOnClick}
										disabled={readOnly || fieldLimitInfo.isLimitReached}
									/>
								</DisabledToolboxContainer>
							</CursorTooltip>
						)}
					</div>
				</div>
			</div>
		</IntlProvider>
	);
};

function ReactFormGenerator({
	disableFooterButtons,
	answer_data,
	activeStep,
	totalSteps,
	variables,
	formIndex,
	read_only: readOnlyProps,
	data,
	enableSelection,
	selectionsDisabled,
	formId,
	display_short
}) {
	const language = 'en';
	const currentAppLocale = AppLocale[language];
	return (
		<IntlProvider locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
			<FormGenerator
				disableFooterButtons={disableFooterButtons}
				answer_data={answer_data}
				activeStep={activeStep}
				totalSteps={totalSteps}
				variables={variables}
				formIndex={formIndex}
				read_only={readOnlyProps}
				data={data}
				enableSelection={enableSelection}
				selectionsDisabled={selectionsDisabled}
				formId={formId}
				display_short={display_short}
			/>
		</IntlProvider>
	);
}

const FormBuilders = {};
FormBuilders.ReactFormBuilder = ReactFormBuilder;
FormBuilders.ReactFormGenerator = ReactFormGenerator;
FormBuilders.ElementStore = store;
FormBuilders.Registry = Registry;

export default FormBuilders;

export { ReactFormBuilder, ReactFormGenerator, store as ElementStore, Registry };
